import { useCallback, useEffect, useMemo, useState } from "react";

import { ThreadV5AdaptiveBuilder } from "../features/thread-v5/ThreadV5AdaptiveBuilder";
import { TailorMannequinAdbTutorial } from "../features/thread-v5/TailorMannequinAdbTutorial";
import { TAILOR_MANNEQUIN_DEMO_AXES } from "../features/thread-v5/tailorMannequinDemoContract";
import "../features/thread-v5/thread-v5.css";
import "./TailorMannequinDemo.css";

type DemoPanel = "full" | "surfaces" | "benchmark";
type DemoPresentationMode = "sequence" | "result";

const OUTPUTS = [
  { value: "CL", label: "CL · lift" },
  { value: "CD", label: "CD · drag" },
  { value: "CY", label: "CY · side force" },
  { value: "Cmx", label: "Cl · roll moment" },
  { value: "Cmy", label: "Cm · pitch moment" },
  { value: "Cmz", label: "Cn · yaw moment" },
] as const;
const AXES = TAILOR_MANNEQUIN_DEMO_AXES.map((axis) => axis.id);

function panelFrom(value: string | null): DemoPanel {
  if (value === "full" || value === "benchmark") return value;
  return "surfaces";
}

function useDemoSearchParams(): [
  URLSearchParams,
  (next: URLSearchParams, options?: { replace?: boolean }) => void,
] {
  const [params, setCurrent] = useState(() => new URLSearchParams(window.location.search));
  useEffect(() => {
    const sync = () => setCurrent(new URLSearchParams(window.location.search));
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);
  const setParams = useCallback((next: URLSearchParams, options?: { replace?: boolean }) => {
    const url = new URL(window.location.href);
    url.search = next.toString();
    window.history[options?.replace ? "replaceState" : "pushState"]({}, "", url);
    setCurrent(new URLSearchParams(next));
  }, []);
  return [params, setParams];
}

export default function TailorMannequinDemo() {
  const [params, setParams] = useDemoSearchParams();
  const panel = panelFrom(params.get("panel"));
  const videoMode = params.get("video") === "1" || params.get("video") === "true";
  const replayMode = params.get("replay") === "1" || params.get("replay") === "true";
  const embedded = videoMode || params.get("embed") === "1" || params.get("embed") === "true";
  const compareLhs = params.get("compare") === "1" || params.get("compare") === "true";
  const presentationMode: DemoPresentationMode = params.get("mode") === "result" ? "result" : "sequence";
  const selfPlaying = panel !== "benchmark" && presentationMode === "sequence" && (replayMode || (!videoMode && params.get("play") !== "0"));
  const autoStart = selfPlaying || params.get("auto") === "1" || (embedded && panel !== "benchmark" && params.get("auto") !== "0");
  const requestedDimensions = Number(params.get("dims"));
  const hasRequestedDimensions = requestedDimensions === 2 || requestedDimensions === 3 || requestedDimensions === 7;
  const dimensions = panel === "benchmark"
    ? hasRequestedDimensions ? requestedDimensions : 2
    : 7;
  const output = OUTPUTS.some((item) => item.value === params.get("output")) ? params.get("output")! : "CL";
  const axisX = AXES.includes(params.get("x") as (typeof AXES)[number]) ? params.get("x")! : "alpha";
  const requestedY = AXES.includes(params.get("y") as (typeof AXES)[number]) ? params.get("y")! : "beta";
  const axisY = requestedY === axisX ? (AXES.find((axis) => axis !== axisX) ?? "beta") : requestedY;

  const setParam = (name: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(name, value);
    setParams(next, { replace: true });
  };
  const startVideoPreset = () => {
    setParams(new URLSearchParams({
      panel: "surfaces",
      mode: "sequence",
      play: "1",
      video: "1",
      replay: "1",
      compare: "0",
      dims: "7",
      output: "CL",
      x: "alpha",
      y: "beta",
    }), { replace: false });
  };
  const embedUrl = useMemo(() => {
    const next = new URLSearchParams(params);
    next.set("embed", "1");
    return `/demos/tailor-mannequin?${next.toString()}`;
  }, [params]);

  return (
    <main className={`tailor-mannequin-demo v5-app is-panel-${panel}${embedded ? " is-embedded" : ""}${selfPlaying ? " is-autoplay" : ""}${videoMode ? " is-video-cut" : ""}${replayMode ? " is-replay" : ""}`} data-testid="tailor-mannequin-demo">
      {!embedded ? (
        <header className="tailor-demo-topbar">
          <div>
            <span className="v5-eyebrow">Adaptive ADB generator</span>
            <h1>Tailor builds the aircraft database</h1>
            <p>Watch Tailor close the loop from completed solve to fitted multi-output ADB to the next useful condition. LHS appears only as a cost-matched, static point-design baseline.</p>
          </div>
          <nav aria-label="Tailor demo views">
            {(["full", "surfaces", "benchmark"] as const).map((view) => (
              <button className={panel === view ? "is-active" : ""} key={view} type="button" onClick={() => setParam("panel", view)}>{view === "benchmark" ? "Compare LHS" : view}</button>
            ))}
            <button type="button" onClick={startVideoPreset}>30s cut</button>
            <a href={embedUrl}>embed this view</a>
          </nav>
        </header>
      ) : null}

      <section className="tailor-demo-view-controls" aria-label="Visible response slice">
        {panel !== "benchmark" ? (
          <>
            <div className="tailor-demo-mode-switch" role="group" aria-label="Demo presentation mode">
              <span>Presentation</span>
              <div>
                <button
                  type="button"
                  className={presentationMode === "sequence" ? "is-active" : ""}
                  aria-pressed={presentationMode === "sequence"}
                  onClick={() => setParam("mode", "sequence")}
                >
                  Sequence
                </button>
                <button
                  type="button"
                  className={presentationMode === "result" ? "is-active" : ""}
                  aria-pressed={presentationMode === "result"}
                  onClick={() => setParam("mode", "result")}
                >
                  End result
                </button>
              </div>
            </div>
            <div className="tailor-demo-mode-switch" role="group" aria-label="LHS comparison visibility">
              <span>Story</span>
              <div>
                <button
                  type="button"
                  className={!compareLhs ? "is-active" : ""}
                  aria-pressed={!compareLhs}
                  onClick={() => setParam("compare", "0")}
                >
                  Tailor only
                </button>
                <button
                  type="button"
                  className={compareLhs ? "is-active" : ""}
                  aria-pressed={compareLhs}
                  onClick={() => setParam("compare", "1")}
                >
                  Compare with LHS
                </button>
              </div>
            </div>
          </>
        ) : null}
        <label><span>Output slice</span><select aria-label="Demo output" value={output} onChange={(event) => setParam("output", event.target.value)}>{OUTPUTS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
        <label><span>X axis</span><select aria-label="Demo x axis" value={axisX} onChange={(event) => setParam("x", event.target.value)}>{TAILOR_MANNEQUIN_DEMO_AXES.map((item) => <option key={item.id} value={item.id}>{item.shortLabel} · {item.label}</option>)}</select></label>
        <label><span>Y axis</span><select aria-label="Demo y axis" value={axisY} onChange={(event) => setParam("y", event.target.value)}>{TAILOR_MANNEQUIN_DEMO_AXES.filter((item) => item.id !== axisX).map((item) => <option key={item.id} value={item.id}>{item.shortLabel} · {item.label}</option>)}</select></label>
        {panel === "benchmark" ? <label><span>Sampling dimensions</span><select aria-label="Demo dimensions" value={dimensions} onChange={(event) => setParam("dims", event.target.value)}><option value={2}>2-D</option><option value={3}>3-D</option><option value={7}>7-D</option></select></label> : null}
        <span>{panel === "benchmark" ? "Any unplotted active axes remain sampled and become live slice sliders." : "The campaign is 7-D. Choose 2D, 3D, or High-D below; every view is a live slice through the same ADB."}</span>
      </section>

      {panel === "benchmark" ? (
        <ThreadV5AdaptiveBuilder
          key={`${panel}-${dimensions}`}
          selectedOutput={output}
          focusXAxis={axisX}
          focusYAxis={axisY}
          onAdoptPoint={() => undefined}
          autoStart={autoStart}
          initialDimensions={dimensions}
          initialStopPostDivergence={false}
        />
      ) : (
        <TailorMannequinAdbTutorial
          key={`${output}-${dimensions}-${axisX}-${axisY}-${compareLhs ? "paired" : "tailor"}-${videoMode ? "video" : "interactive"}-${replayMode ? "replay" : "brief"}`}
          output={output}
          dimensions={dimensions}
          focusXAxis={axisX}
          focusYAxis={axisY}
          presentationMode={presentationMode}
          comparisonEnabled={compareLhs}
          videoMode={videoMode}
          replayMode={replayMode}
        />
      )}
    </main>
  );
}
